import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ArrowRight } from 'lucide-react';
import { adminLogin, verifyAdmin } from '../lib/api';
import { toast } from 'sonner';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Check if already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          await verifyAdmin();
          navigate('/admin/dashboard');
        } catch {
          localStorage.removeItem('admin_token');
        }
      }
      setChecking(false);
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await adminLogin(credentials);
      localStorage.setItem('admin_token', response.data.token);
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="h-screen bg-dark flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-accent-purple border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-accent-purple/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="text-accent-purple" size={28} />
          </div>
          <h1 className="font-heading text-3xl mb-2">Admin Access</h1>
          <p className="text-slate-500">Enter your credentials to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="glass rounded-2xl p-8">
          <div className="mb-6">
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Username</label>
            <input
              type="text"
              required
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="input-underline"
              placeholder="Enter username"
              data-testid="admin-username-input"
            />
          </div>

          <div className="mb-8">
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Password</label>
            <input
              type="password"
              required
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              className="input-underline"
              placeholder="Enter password"
              data-testid="admin-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-cinematic bg-white text-black w-full py-4 rounded-full text-sm font-bold tracking-widest uppercase hover:bg-slate-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {isLoading ? 'Signing in...' : (
              <>
                Sign In
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-sm text-slate-600">
          <a href="/" className="hover:text-white transition-colors">← Back to website</a>
        </p>
      </motion.div>
    </div>
  );
}
